/**
 * Leadler state: the Harita and Huni read model, the Öğrenme view model and the learning
 * decisions. Loaded when the Leadler screen opens, not with the snapshot. The rules of
 * learning-rules.ts are enforced here, not only in the buttons:
 * - Geri al only for reversible automatic changes; suppression and opt-out phrases never;
 *   a written reason always, and the completed hold when the revert loosens;
 * - the learning batch: approving needs the reason and the batch hold, an H3 item its own
 *   hold, H2 items never; a rejection needs a reason code;
 * - no learning decision on stale data (the same live-view rule as loosening a policy);
 * - after any refused or unanswered decision the view is read again before retrying.
 */

import { useSyncExternalStore } from 'react';
import { type TwinApi, TwinApiError } from '../api/client.js';
import type { Basemap, CellDetail, LeadDetail, LeadsView } from '../api/leads.js';
import type {
  FitLabel,
  LearningBatchInput,
  LearningBatchResult,
  LearningItemDecision,
  LearningView,
} from '../api/learning.js';
import { batchViolation, revertViolation } from '../api/learning-rules.js';
import { reasonText } from '../api/policy-rules.js';
import type { Instant } from '../api/types.js';
import { useStore } from './context.js';

export interface LeadsState {
  leads: LeadsView | null;
  leadsError: string | null;
  learning: LearningView | null;
  learningError: string | null;
  /** A learning decision is in flight; its controls stay disabled. */
  busy: boolean;
  /** The company card open on the map (GET /leads/{leadId}). */
  selectedLeadId: string | null;
  selectedLead: LeadDetail | null;
  selectedLoading: boolean;
  selectedError: string | null;
  /** The cell card open on the map (GET /leads/cells/{cellId}); also the list's cell filter. */
  selectedCellId: string | null;
  selectedCell: CellDetail | null;
  selectedCellLoading: boolean;
  selectedCellError: string | null;
  /** Basemap layers per region (GET /leads/basemap/{region}); null while loading or after a failed load. */
  basemaps: Record<string, Basemap | null>;
  basemapErrors: Record<string, string>;
}

/** What the leads store needs from the panel store. */
export interface LeadsHost {
  /** Live stream and a fresh heartbeat: the same rule as loosening a policy. */
  canLoosen(): boolean;
  showToast(
    message: string,
    options?: { tone?: 'ink' | 'danger'; icon?: 'check' | 'pause' | 'alert' | 'info' },
  ): string;
  now(): Instant;
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export class LeadsStore {
  private state: LeadsState = {
    leads: null,
    leadsError: null,
    learning: null,
    learningError: null,
    busy: false,
    selectedLeadId: null,
    selectedLead: null,
    selectedLoading: false,
    selectedError: null,
    selectedCellId: null,
    selectedCell: null,
    selectedCellLoading: false,
    selectedCellError: null,
    basemaps: {},
    basemapErrors: {},
  };
  private readonly listeners = new Set<() => void>();
  private loading: Promise<void> | null = null;

  constructor(
    private readonly api: TwinApi,
    private readonly host: LeadsHost,
  ) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getState = (): LeadsState => this.state;

  private set(patch: Partial<LeadsState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  /** Loads once; the screen calls it on mount. */
  ensureLoaded(): Promise<void> {
    if (this.state.leads || this.state.learning || this.loading) return this.loading ?? Promise.resolve();
    return this.reload();
  }

  /** Reads both models again (after a decision, a refusal, or Tekrar dene). */
  reload(): Promise<void> {
    const run = (async () => {
      const [leads, learning] = await Promise.allSettled([this.api.getLeads(), this.api.getLearning()]);
      this.set({
        leads: leads.status === 'fulfilled' ? leads.value : this.state.leads,
        leadsError: leads.status === 'rejected' ? errorText(leads.reason) : null,
        learning: learning.status === 'fulfilled' ? learning.value : this.state.learning,
        learningError: learning.status === 'rejected' ? errorText(learning.reason) : null,
      });
    })();
    this.loading = run;
    void run.finally(() => {
      if (this.loading === run) this.loading = null;
    });
    return run;
  }

  /** Opens (or closes with null) the company card of a lead; a stale answer never overwrites a newer choice. */
  async selectLead(leadId: string | null): Promise<void> {
    if (leadId === null) {
      this.set({ selectedLeadId: null, selectedLead: null, selectedLoading: false, selectedError: null });
      return;
    }
    this.set({ selectedLeadId: leadId, selectedLead: null, selectedLoading: true, selectedError: null });
    try {
      const detail = await this.api.getLeadDetail(leadId);
      if (this.state.selectedLeadId === leadId) this.set({ selectedLead: detail, selectedLoading: false });
    } catch (error) {
      if (this.state.selectedLeadId === leadId) this.set({ selectedLoading: false, selectedError: errorText(error) });
    }
  }

  /** Opens (or closes with null) the cell card; a stale answer never overwrites a newer choice. */
  async selectCell(cellId: string | null): Promise<void> {
    if (cellId === null) {
      this.set({ selectedCellId: null, selectedCell: null, selectedCellLoading: false, selectedCellError: null });
      return;
    }
    this.set({ selectedCellId: cellId, selectedCell: null, selectedCellLoading: true, selectedCellError: null });
    try {
      const detail = await this.api.getCellDetail(cellId);
      if (this.state.selectedCellId === cellId) this.set({ selectedCell: detail, selectedCellLoading: false });
    } catch (error) {
      if (this.state.selectedCellId === cellId) {
        this.set({ selectedCellLoading: false, selectedCellError: errorText(error) });
      }
    }
  }

  /** Loads a region's basemap once; a failure leaves the map without it (the cells still draw). */
  async loadBasemap(region: string): Promise<void> {
    if (region === '' || region in this.state.basemaps) return;
    this.set({ basemaps: { ...this.state.basemaps, [region]: null } });
    try {
      const basemap = await this.api.getBasemap(region);
      this.set({ basemaps: { ...this.state.basemaps, [region]: basemap } });
    } catch (error) {
      this.set({ basemapErrors: { ...this.state.basemapErrors, [region]: errorText(error) } });
    }
  }

  /** Why a learning decision cannot be made right now, or null. */
  decisionBlocker(): string | null {
    if (!this.host.canLoosen()) return 'Bağlantı bekleniyor';
    if (this.state.busy) return 'Kaydediliyor…';
    return null;
  }

  private refused(error: unknown, fallback: string): void {
    const message =
      error instanceof TwinApiError && error.message.trim() !== ''
        ? error.message
        : `${fallback} · bağlantıyı kontrol et`;
    this.host.showToast(`Kaydedilmedi · ${message}`, { tone: 'danger', icon: 'alert' });
    // A write may have landed (no answer, 5xx) or the screen was out of date: read first.
    void this.reload();
  }

  /** Geri al. Returns true when the server recorded the revert. */
  async revert(
    changeId: string,
    options: { reason?: string | null; confirmedByHold?: boolean } = {},
  ): Promise<boolean> {
    const change = this.state.learning?.changes.find((item) => item.changeId === changeId);
    const input = {
      changeId,
      reason: reasonText(options.reason) === '' ? null : reasonText(options.reason),
      confirmedByHold: options.confirmedByHold === true,
    };
    const refused = revertViolation(change, input);
    if (refused) {
      this.host.showToast(refused.message, { tone: 'danger', icon: 'alert' });
      return false;
    }
    const blocker = this.decisionBlocker();
    if (blocker) {
      this.host.showToast(`${blocker} · geri alma canlı veriyle yapılır`, { tone: 'danger', icon: 'alert' });
      return false;
    }
    this.set({ busy: true });
    try {
      const result = await this.api.revertLearningChange(input);
      const learning = this.state.learning;
      if (learning) {
        this.set({
          learning: {
            ...learning,
            changes: learning.changes.map((item) =>
              item.changeId === changeId ? { ...item, revertedAt: result.revertedAt, revertedBy: 'Sercan' } : item,
            ),
          },
        });
      }
      this.host.showToast(`Geri alındı · ${change?.objectLabel ?? changeId} · gerekçe kayda yazıldı`, {
        icon: 'check',
      });
      return true;
    } catch (error) {
      this.refused(error, 'Geri alınamadı');
      return false;
    } finally {
      this.set({ busy: false });
    }
  }

  /**
   * One decision on the learning batch. Deferred items stay for next week. The server
   * re-checks every rule and the payload hash; a refusal reloads the batch.
   */
  async decideBatch(
    decisions: LearningItemDecision[],
    options: { reason?: string | null; confirmedByHold?: boolean } = {},
  ): Promise<LearningBatchResult | null> {
    const batch = this.state.learning?.batch ?? null;
    if (!batch) return null;
    const reason = reasonText(options.reason);
    const input: LearningBatchInput = {
      batchId: batch.batchId,
      payloadHash: batch.payloadHash,
      decisions,
      reason: reason === '' ? null : reason,
      confirmedByHold: options.confirmedByHold === true,
    };
    const refused = batchViolation(batch, input, this.host.now());
    if (refused) {
      this.host.showToast(refused.message, { tone: 'danger', icon: 'alert' });
      return null;
    }
    const blocker = this.decisionBlocker();
    if (blocker) {
      this.host.showToast(`${blocker} · öğrenme kararları canlı veriyle verilir`, { tone: 'danger', icon: 'alert' });
      return null;
    }
    this.set({ busy: true });
    try {
      const result = await this.api.decideLearningBatch(input);
      this.host.showToast(
        `Öğrenme paketi kaydedildi · ${result.approved} kabul, ${result.rejected} ret, ${result.deferred} sonraya`,
        { icon: 'check' },
      );
      await this.reload();
      return result;
    } catch (error) {
      this.refused(error, 'Paket kaydedilemedi');
      return null;
    } finally {
      this.set({ busy: false });
    }
  }

  /** A fit label; blind echoes whether the score was hidden on the screen. */
  async labelFit(taskId: string, label: FitLabel): Promise<boolean> {
    const task = this.state.learning?.fit.labelTasks.find((item) => item.taskId === taskId);
    if (!task) return false;
    if (!this.host.canLoosen()) {
      this.host.showToast('Bağlantı bekleniyor · etiket canlı veriyle kaydedilir', { tone: 'danger', icon: 'alert' });
      return false;
    }
    try {
      await this.api.labelFit({ taskId, label, blind: task.blind });
      const learning = this.state.learning;
      if (learning) {
        this.set({
          learning: {
            ...learning,
            fit: { ...learning.fit, labelTasks: learning.fit.labelTasks.filter((item) => item.taskId !== taskId) },
          },
        });
      }
      this.host.showToast(`Etiket kaydedildi · ${task.company}`, { icon: 'check' });
      return true;
    } catch (error) {
      this.refused(error, 'Etiket kaydedilemedi');
      return false;
    }
  }
}

export function useLeads(): { store: LeadsStore; state: LeadsState } {
  const store = useStore().leads;
  const state = useSyncExternalStore(store.subscribe, store.getState, store.getState);
  return { store, state };
}
